import React from 'react';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';

import userFormStyle from '../assets/jss/crm/views/userFormStyle';

import { createStdRequest, apiFetch, validateEmail, validateAcceptablePassword, showAlert } from '../utils/utils';

import Modal from './Modal';
import CrmTextField from './CrmTextField';
import PhoneInput from './PhoneInput';
import AlertModal from './AlertModal';

const localStyles = {
  ...userFormStyle,
  textFieldHolder: {
    marginTop: '20px',
  },
};

class RegisterUserModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      redirectToHome: false,
      firstName: '',
      middleNames: '',
      lastName: '',
      preferredName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phoneRoles: ['Mobile'],
      phoneNumber: '',
    };
  }

  possiblePhoneRoles = ['Mobile'];

  validationPolicy = {
    requireFirm: true,
    requireFirstName: true,
    requireLastName: true,
    requirePreferredName: false,
    requireEmail: true,
    requirePassword: true,
    requirePasswordMatch: true,
    requirePhone: false,
  };

  handleTextChange(event) {
    const id = event.target.id;
    const forbidWhiteSpace = id === 'email' || id === 'password' || id === 'confirmPassword';
    const rawValue = event.target.value;
    const value = forbidWhiteSpace ? rawValue.trim() : rawValue;
    this.setState({ [id]: value });
  }

  handlePhoneRolesChange(rolesObj) {
    let rolesArray = [];
    const keys = Object.keys(rolesObj);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (rolesObj[key]) {
        rolesArray = [...rolesArray, key];
      }
    }
    this.setState({ phoneRoles: rolesArray });
  }

  handlePhoneNumberChange(newNumber) {
    this.setState({ phoneNumber: newNumber });
    console.log(`newNumber: ${newNumber}`);
  }

  createApiStruct() {
    const { firmId, forAdmin } = this.props;
    const rolesStr = forAdmin ? 'Admin' : 'User';
    const profferedPreferredName = this.state.preferredName.trim();
    const profferedFirstName = this.state.firstName.trim();
    const preferredName = profferedPreferredName || profferedFirstName;
    const firstName = profferedFirstName || preferredName;
    const phones = this.state.phoneNumber
      ? [{ roles: 'Mobile', countryCode: '1', number: this.state.phoneNumber }]
      : [];
    return {
      personId: 0,
      firmId: firmId,
      roles: rolesStr,
      email: this.state.email,
      password: this.state.password,
      firstName,
      middleNames: this.state.middleNames.trim(),
      lastName: this.state.lastName.trim(),
      preferredName,
      sex: 'Unknown',
      birthDate: null,
      latestContactDate: null,
      officialId: null,
      phoneNumbers: phones,
      addresses: [],
    };
  }

  validateForm() {
    if (this.validationPolicy.requireFirm) {
      if (!this.props.firmId) {
        return {
          valid: false,
          errorMessage: 'You must be logged in as a Admin of some Provider',
        };
      }
    }

    if (this.validationPolicy.requireFirstName) {
      if (!this.state.firstName.trim()) {
        return {
          valid: false,
          errorMessage: 'You must supply a First Name',
        };
      }
    }

    if (this.validationPolicy.requireLastName) {
      if (!this.state.lastName.trim()) {
        return {
          valid: false,
          errorMessage: 'You must supply a Last Name',
        };
      }
    }

    if (this.validationPolicy.requirePreferredName) {
      if (!this.state.preferredName.trim()) {
        return {
          valid: false,
          errorMessage: 'You must supply a Preferred Name',
        };
      }
    }

    if (this.validationPolicy.requireEmail) {
      if (!this.state.email) {
        return {
          valid: false,
          errorMessage: 'You must supply an Email address',
        };
      }
      if (!validateEmail(this.state.email)) {
        return {
          valid: false,
          errorMessage: 'You must supply a plausible Email address',
        };
      }
    }

    if (this.validationPolicy.requirePassword) {
      if (!this.state.password) {
        return {
          valid: false,
          errorMessage: 'You must supply a password',
        };
      }
      const passwordValidation = validateAcceptablePassword(this.state.password);
      if (!passwordValidation.valid) {
        return passwordValidation;
      }
    }

    if (this.validationPolicy.requirePasswordMatch) {
      if (!this.state.confirmPassword) {
        return {
          valid: false,
          errorMessage: "You must enter your password again on the 'Confirm Password' line",
        };
      }
      if (this.state.password !== this.state.confirmPassword) {
        return {
          valid: false,
          errorMessage: "Your password confirmation doesn't match your password",
        };
      }
    }
    if (this.validationPolicy.requirePhone) {
      if (!this.state.phoneNumber) {
        return {
          valid: false,
          errorMessage: 'You must provide a phone number',
        };
      }
    }

    if (this.state.phoneNumber) {
      if (!this.haveValidPhone()) {
        return {
          valid: false,
          errorMessage: 'If you provide a phone number, it must be in format: (aaa) eee-nnnn',
        };
      }
    }

    return { valid: true };
  }

  phoneRegEx = /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/;

  haveValidPhone() {
    if (!this.state.phoneNumber) {
      return false;
    }
    return this.phoneRegEx.test(this.state.phoneNumber);
  }

  clearForm() {
    this.setState({
      firstName: '',
      middleNames: '',
      lastName: '',
      preferredName: '',
      email: '',
      password: '',
      confirmPassword: '',
    });
  }

  acceptForm() {
    const validation = this.validateForm();
    if (!validation.valid) {
      showAlert(this, validation.errorMessage);
      return;
    }
    const apiStruct = this.createApiStruct();
    const uri = '/api/user';
    const request = createStdRequest(uri, 'POST', apiStruct);
    const actionOnGoodResponse = (json) => {
      const { success, user } = json;
      if (success) {
        this.clearForm();
        this.props.onSuccess({ user });
      } else {
        showAlert(this, json.errorMessage);
      }
    };
    apiFetch(this.props.page, request, actionOnGoodResponse);
  }

  cancelForm() {
    this.props.onClose({ action: 'canceled' });
  }

  render() {
    const { classes, show, forAdmin } = this.props;
    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <Modal
          open={show}
          title={forAdmin ? 'Register Admin' : 'Register User'}
          okButtonLabel="Register"
          cancelButtonLabel="Cancel"
          onOk={() => this.acceptForm()}
          onCancel={() => this.cancelForm()}
        >
          <Grid container>
            <Grid item xs={12} sm={12} md={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <div className={classes.textFieldHolder}>
                    <CrmTextField
                      label="First Name"
                      id="firstName"
                      required
                      fullWidth
                      value={this.state.firstName}
                      onChange={(event) => this.handleTextChange(event)}
                    />
                  </div>
                  <div className={classes.textFieldHolder}>
                    <CrmTextField
                      label="Middle Name(s)"
                      id="middleNames"
                      fullWidth
                      value={this.state.middleNames}
                      onChange={(event) => this.handleTextChange(event)}
                    />
                  </div>
                  <div className={classes.textFieldHolder}>
                    <CrmTextField
                      label="Last Name"
                      id="lastName"
                      required
                      fullWidth
                      value={this.state.lastName}
                      onChange={(event) => this.handleTextChange(event)}
                    />
                  </div>
                  <div className={classes.textFieldHolder}>
                    <CrmTextField
                      label="Preferred Name"
                      id="preferredName"
                      fullWidth
                      value={this.state.preferredName}
                      onChange={(event) => this.handleTextChange(event)}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <div className={classes.textFieldHolder}>
                    <CrmTextField
                      label="Email Address"
                      id="email"
                      required
                      fullWidth
                      value={this.state.email}
                      onChange={(event) => this.handleTextChange(event)}
                    />
                  </div>
                  <div className={classes.textFieldHolder}>
                    <CrmTextField
                      label="Password"
                      id="password"
                      required
                      fullWidth
                      password
                      value={this.state.password}
                      onChange={(event) => this.handleTextChange(event)}
                    />
                  </div>
                  <div className={classes.textFieldHolder}>
                    <CrmTextField
                      label="Confirm Password"
                      id="confirmPassword"
                      required
                      fullWidth
                      password
                      value={this.state.confirmPassword}
                      onChange={(event) => this.handleTextChange(event)}
                    />
                  </div>
                  <div className={classes.textFieldHolder}>
                    <PhoneInput
                      possibleRoles={this.possiblePhoneRoles}
                      actualRoles={this.state.phoneRoles}
                      number={this.state.phoneNumber}
                      onRolesChange={(rolesObj) => this.handlePhoneRolesChange(rolesObj)}
                      onNumberChange={(newNumber) => this.handlePhoneNumberChange(newNumber)}
                    />
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Modal>
      </div>
    );
  }
}

export default withStyles(localStyles)(RegisterUserModal);
